<template>
  <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon @click="closeDialog">
          <v-icon>clear</v-icon>
        </v-btn>
        <v-toolbar-title>Coupon</v-toolbar-title>
        <div class="flex-grow-1" />
      </v-toolbar>
      <v-form v-model="Form.valid">
        <v-container grid-list-xs :class="$vuetify.breakpoint.mdAndUp ? 'pa-5' : ''">
          <v-layout wrap>
            <v-flex xs12 md3>
              <v-text-field
                type="text"
                label="Code"
                v-model="Form.code"
                mask="NNNNNNNNN"
                v-validate="'checkUpperCaseAndNumber|min:3|required'"
                :error-messages="errors.collect('code')"
                data-vv-name="code"
                hint="** If you would like to create your custom Coupon Code ( least 3 Characters A-Z 0-9 ) **"
                persistent-hint
              />
            </v-flex>
            <v-flex xs12 md5 offset-md1>
              <v-text-field
                label="Description"
                v-model="Form.description"
                auto-grow
                v-validate="'required'"
                :error-messages="errors.collect('description')"
                data-vv-name="description"
                clearable
              />
            </v-flex>
            <v-flex xs12 md2 offset-md1>
              <v-text-field label="Refresh Quota" v-model="refreshQuota" disabled />
            </v-flex>
            <v-flex xs12>
              <v-flex>
                <label style="color: rgba(0, 0, 0, 0.6); font-size: 12px">Tag</label>
              </v-flex>
              <v-flex>
                <v-chip v-for="tag in tags" :key="tag.id" style="margin-right: 10px">{{ tag.name }} </v-chip>
              </v-flex>
            </v-flex>
            <v-flex class="coupon-detail-meta-table" xs12>
              <v-data-table
                class="full-width full-height"
                :loading="loading"
                :headers="headers"
                :items="Form.meta"
                :options.sync="couponsMetaPagination"
                :server-items-length="totalCount"
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
      <v-card-actions class="delete-coupon-bt">
        <v-btn color="error" @click="deleteCoupon(Form)">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style>
.delete-coupon-bt {
  position: fixed;
  bottom: 54px;
  right: 96px;
}

.coupon-detail-meta-table {
  margin-top: 50px;
}

.coupon-detail-top-bt {
  margin-left: auto;
  margin-right: 30px;
}
</style>

<script>
import Forms from '../form/form';
import formMixin from '../Mixins/form';
import * as R from 'ramda';
import couponService from '../services/coupon';
import dateUtil from '../utils/dateTime';
import couponTransformer from '../transformers/coupon';
import countryMixin from '../Mixins/country';

export default {
  mixins: [formMixin, countryMixin],
  props: {
    couponDetail: Object,
  },
  data() {
    return {
      dialog: R.isEmpty(this.couponDetail),
      selected: [],
      couponsMetaPagination: {},
      totalCount: 0,
      loading: false,
      tags: [],
      Form: new Forms({
        data: () => ({
          id: null,
          code: null,
          description: null,
          meta: [],
        }),
        transformToPayload: couponTransformer.create,
      }),
      headers: [
        { text: 'Total usage limit', value: 'total_usage_limit' },
        { text: 'Individual usage limit', value: 'individual_usage_limit' },
        { text: 'Discount amount', value: 'discount_amount' },
        { text: 'Discount limit', value: 'discount_limit' },
        { text: 'Discount unit', value: 'discount_unit' },
        { text: 'Terms link url', value: 'terms_link_url' },
        { text: 'Promotion start at', value: 'promotion_start_at' },
        { text: 'Promotion end at', value: 'promotion_end_at' },
        { text: 'Currency', value: 'currency' },
      ],
    };
  },

  computed: {
    computedDateFormatted() {
      return dateUtil.formatDate(this.Form.date);
    },
    discountValidation() {
      let validateRule = {
        numeric: true,
        required: true,
      };
      if (this.Form.discount_unit === 'percentage') {
        validateRule.max_value = 100;
      }
      return validateRule;
    },
    refreshQuota() {
      if (this.Form.meta[0] && this.Form.meta[0].refresh_quota) {
        return this.Form.meta[0].refresh_quota;
      } else {
        return 'None';
      }
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.getCouponMeta().then((result) => {
          this.Form.meta = result.CouponMeta;
          this.totalCount = result.totalCount;
        });
      }
    },
    couponsMetaPagination: {
      handler() {
        this.getCouponMeta().then((result) => {
          this.Form.meta = result.CouponMeta;
          this.totalCount = result.totalCount;
        });
      },
      deep: true,
    },
    couponDetail: {
      handler(val) {
        if (val) {
          this.Form.id = val.id;
          this.Form.code = val.code;
          this.Form.description = val.description;
          this.dialog = !R.isEmpty(val);
          this.getCouponData();
        }
      },
    },
  },

  methods: {
    getCouponData() {
      var self = this;
      couponService
        .getCoupon(this.couponDetail.coupon_id)
        .then((couponRes) => {
          self.tags = couponRes.data.tags;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getCouponMeta() {
      this.loading = true;
      return new Promise((resolve, reject) => {
        const { sortBy, descending, page, itemsPerPage } = this.couponsMetaPagination;
        let query = {};
        if (itemsPerPage !== -1) {
          query.perPage = itemsPerPage;
          query.page = page;
        }
        if (sortBy) {
          query.order_by = sortBy;
          query.order = descending ? 'desc' : 'asc';
        }
        if (this.countrySelected) query.country = this.countrySelected;
        if (this.search) query.search = this.search;
        query.couponId = this.couponDetail.coupon_id;
        couponService
          .getMeta(query)
          .then((resultList) => {
            this.loading = false;
            resolve({
              CouponMeta: resultList.data,
              totalCount: resultList.totalCount,
            });
          })
          .catch((err) => {
            this.loading = false;
            reject(err);
          });
      });
    },

    deleteCoupon(coupon) {
      this.$root
        .$confirm('Delete', `Are you sure for deleting <b>${coupon.code}</b> ?`, { color: 'error' })
        .then((confirm) => {
          if (confirm) {
            couponService.deletes(coupon.id).then(() => {
              this.$notify({
                title: 'Delete',
                text: `Delete ${coupon.code} success !!`,
                type: 'success',
              });
              this.closeDialog();
              this.$emit('success');
            });
          }
        });
    },

    closeDialog() {
      this.resetFormError(null, this.Form);
      this.$emit('closeCouponDetail');
      this.dialog = false;
    },
  },
  components: {},
};
</script>
